import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HardDrive, Store, ChevronDown, ChevronUp, Info, AlertCircle, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export const AdminEtsyDriveSettings = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasEtsyConnection, setHasEtsyConnection] = useState<boolean | null>(null);
  const [googleDriveConfigured, setGoogleDriveConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    const checkConnections = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check Etsy connection
      const { data: etsyData } = await supabase
        .from('etsy_connections')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      setHasEtsyConnection(!!etsyData);
      
      // For Google Drive, we can only check if the feature is available
      // The actual secrets are in Supabase Edge Function secrets
      setGoogleDriveConfigured(null); // Unknown - requires secrets check
    };

    checkConnections();
  }, []);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/20">
                  <Store className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">Etsy & Drive Integration Status</CardTitle>
                  <CardDescription className="text-xs">
                    View integration configuration and connection status
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={hasEtsyConnection ? "default" : "secondary"} className="text-xs">
                  <Store className="h-3 w-3 mr-1" /> 
                  {hasEtsyConnection ? 'Etsy Connected' : 'Etsy Not Connected'}
                </Badge>
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            {/* Google Drive Section */}
            <div className="space-y-3 p-4 rounded-lg bg-background/50 border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <HardDrive className="h-5 w-5 text-blue-500" />
                  <div>
                    <Label className="font-medium">Google Drive Upload</Label>
                    <p className="text-xs text-muted-foreground">
                      Requires Supabase Edge Function secrets configuration
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs">
                  <HardDrive className="h-3 w-3 mr-1" /> Check Secrets
                </Badge>
              </div>
              
              <div className="mt-3 p-3 rounded bg-blue-500/10 border border-blue-500/20 text-xs">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-blue-400">Required Secrets</p>
                    <p className="text-muted-foreground mt-1">
                      Configure in Supabase Dashboard → Edge Functions → Secrets:
                    </p>
                    <ul className="list-disc list-inside mt-2 text-muted-foreground space-y-1">
                      <li><code className="bg-background px-1 rounded">GOOGLE_DRIVE_SERVICE_ACCOUNT</code> - JSON key file content</li>
                      <li><code className="bg-background px-1 rounded">GOOGLE_DRIVE_FOLDER_ID</code> - Target folder ID from Drive URL</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Etsy Section */}
            <div className="space-y-4 p-4 rounded-lg bg-background/50 border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Store className="h-5 w-5 text-orange-500" />
                  <div>
                    <Label className="font-medium">Etsy Integration</Label>
                    <p className="text-xs text-muted-foreground">
                      OAuth connection for auto-listing generated images
                    </p>
                  </div>
                </div>
                <Badge 
                  variant={hasEtsyConnection ? "default" : "secondary"} 
                  className={`text-xs ${hasEtsyConnection ? 'bg-green-500/20 text-green-400 border-green-500/30' : ''}`}
                >
                  {hasEtsyConnection ? 'Connected' : 'Not Connected'}
                </Badge>
              </div>

              {hasEtsyConnection === false && (
                <div className="p-3 rounded bg-yellow-500/10 border border-yellow-500/20 text-xs">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-400">Etsy Not Connected</p>
                      <p className="text-muted-foreground mt-1">
                        Users need to connect their Etsy account in Settings → Integrations to enable auto-listing.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {hasEtsyConnection && (
                <div className="p-3 rounded bg-green-500/10 border border-green-500/20 text-xs">
                  <div className="flex items-start gap-2">
                    <Store className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-green-400">Etsy Connected</p>
                      <p className="text-muted-foreground mt-1">
                        Auto-listing is available for automation jobs. Configure listing templates per-job in the Automation Agent.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-3 p-3 rounded bg-muted/30 border text-xs">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Per-Job Configuration</p>
                    <p className="text-muted-foreground mt-1">
                      Etsy listing templates (title, description, price, tags) are configured per automation job in the Automation Agent section.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
